// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Per-tee hole lengths, between the course-config form and the wire.
//
// The form holds them as strings keyed by the tee row's own id — that key survives a rename,
// which is the whole reason it is not the tee's name. The save has to speak the other way
// round: the store matches tees by name and sex, because a tee added in this session has no
// id yet. Both translations live here rather than inside the screen, since a mistake in
// either shows up as a silently empty column rather than as an error.

/** A tee as the form holds it: a local row id, and the name/sex the store matches on. */
export type TeeIdentity = { id: string; name: string; sex: string };

/** One hole's length from one tee, as the course detail reports it. */
export type StoredTeeLength = { teeId: string | null; lengthMeters: number | null };

/** One hole's length from one tee, as the upsert wants it. */
export type TeeLengthPayload = { teeName: string; sex: string; lengthMeters: number };

/**
 * The form's per-tee cells for one hole, from what the server returned.
 *
 * Rows with no tee id or no length are dropped: an unattributable number would show up under
 * whichever column happened to be first, which is worse than not showing it at all.
 */
export const teeLengthsFromStored = (
  stored: readonly StoredTeeLength[]
): Record<string, string> => {
  const cells: Record<string, string> = {};
  for (const row of stored) {
    if (row.teeId && row.lengthMeters && row.lengthMeters > 0) {
      cells[row.teeId] = String(row.lengthMeters);
    }
  }
  return cells;
};

/**
 * The upsert payload for one hole's per-tee lengths.
 *
 * Blank cells and unnamed tees are left out rather than sent as zero — the store reads an
 * absent entry as "not measured", and there is no other way to say that. The list is returned
 * even when empty, which is how clearing the last length actually takes: a missing list means
 * "not stated" and leaves the stored rows alone.
 */
export const teeLengthsForSave = (
  cells: Record<string, string>,
  tees: readonly TeeIdentity[]
): TeeLengthPayload[] =>
  tees
    .map((tee) => ({
      teeName: tee.name.trim(),
      sex: tee.sex.trim().toLowerCase(),
      lengthMeters: Number.parseInt(cells[tee.id] ?? '', 10),
    }))
    .filter(
      (entry) =>
        entry.teeName.length > 0 &&
        Number.isFinite(entry.lengthMeters) &&
        entry.lengthMeters > 0
    );

/** Whether any cell holds something typed that is not a usable length. Blank is fine. */
export const hasInvalidTeeLength = (cells: Record<string, string>): boolean =>
  Object.values(cells).some((value) => value.trim() !== '' && !(Number.parseInt(value, 10) > 0));
